/**
 * Agent Manager
 * 
 * Core orchestration class that manages agent lifecycle, transitions,
 * WebSocket connections, and conversation history.
 * 
 * Supports two modes:
 * 1. Legacy mode: Single API key, one client for all agents
 * 2. Ephemeral token mode: Per-agent tokens, new client on each transition
 */

import { EventEmitter } from 'eventemitter3';
import { LiveConnectConfig } from '@google/genai';
import { GenAILiveClient } from '../lib/genai-live-client';
import { logger } from '../utils/logger';
import { fetchGeminiToken, TokenContextData } from '../lib/token-service';
import { AgentConnectionPool, createConnectionPool, PooledConnection } from '../lib/agent-connection-pool';
import { TokenResponse } from '../types';
import {
  AgentNode,
  AgentContext,
  AgentManagerState,
  AgentTransitionEvent,
  TransitionData,
  ConversationHistory,
  ToolCallResult,
  SupportedLanguage,
  VoiceGender,
  buildLiveConfig,
  createInitialContext,
  addToHistory,
  getHistoryText,
} from './types';
import { validateEntryGuardrails, findValidTransition } from './guardrails';

/**
 * Events emitted by AgentManager
 */
export interface AgentManagerEvents {
  // Agent lifecycle events
  agentActivated: (agentId: string, context: AgentContext) => void;
  agentDeactivated: (agentId: string, context: AgentContext) => void;
  
  // Transition events
  transitionStarted: (event: AgentTransitionEvent) => void;
  transitionCompleted: (event: AgentTransitionEvent) => void;
  transitionFailed: (event: AgentTransitionEvent, errors: string[]) => void;
  
  // Connection events
  connected: () => void;
  disconnected: () => void;
  reconnecting: (agentId: string) => void;
  
  // State change
  stateChanged: (state: AgentManagerState) => void;
  
  // Tool call events (forwarded from agent)
  toolCall: (result: ToolCallResult) => void;
  
  // Flow completion events (NEW - for embedded mode)
  flowCompleted: (flowType: string, result: any, agentId: string) => void;
  
  // Error events
  error: (error: Error) => void;
}

/**
 * AgentManager configuration
 */
export interface AgentManagerConfig {
  client: GenAILiveClient;
  model: string;
  initialAgentId: string;
  agents: Map<string, AgentNode>;
  userData?: TransitionData['userData'];
  voiceGender?: VoiceGender;
  userLocation?: TransitionData['userLocation'];
  /** When set (e.g. from text chatbot), FAQ agent starts with this language; entry guardrail passes */
  language?: SupportedLanguage;
  // Ephemeral token mode configuration
  useEphemeralTokens?: boolean;
  backendUrl?: string;
}

/**
 * AgentManager - Orchestrates multi-agent flows
 */
export class AgentManager extends EventEmitter<AgentManagerEvents> {
  private client: GenAILiveClient;
  private model: string;
  private agents: Map<string, AgentNode>;
  private state: AgentManagerState;
  private isInitialized: boolean = false;
  
  // Ephemeral token mode configuration
  private useEphemeralTokens: boolean = false;
  private backendUrl: string = '';
  private voiceGender: VoiceGender = 'male';
  
  // Connection pool for zero-latency transitions
  private connectionPool: AgentConnectionPool | null = null;
  
  // Flag to suppress disconnect events during seamless pool transitions
  private isSeamlessTransition: boolean = false;

  constructor(config: AgentManagerConfig) {
    super();
    
    this.client = config.client;
    this.model = config.model;
    this.agents = config.agents;
    this.useEphemeralTokens = config.useEphemeralTokens || false;
    this.backendUrl = config.backendUrl || '';
    this.voiceGender = config.voiceGender || 'male';
    
    // Initialize state
    this.state = {
      currentAgentId: null,
      context: createInitialContext(config.userData, config.voiceGender),
      isConnected: false,
      isTransitioning: false,
      transitionHistory: [],
    };
    
    // Set initial location if provided
    if (config.userLocation) {
      this.state.context.transitionData.userLocation = config.userLocation;
    }
    // Set initial language when starting at FAQ (e.g. from text chatbot)
    if (config.language) {
      this.state.context.transitionData.language = config.language;
    }
    
    // Bind client events
    this.setupClientEvents();
    
    // Initialize connection pool for ephemeral token mode
    if (this.useEphemeralTokens && this.backendUrl) {
      this.connectionPool = createConnectionPool({
        backendUrl: this.backendUrl,
        voiceGender: this.voiceGender,
        maxPoolSize: 5,
        tokenTTL: 45000, // 45 seconds
      });
      logger.info('[AgentManager] Connection pool initialized for zero-latency transitions');
    }
    
    // Listen for auto-transition events from agents (e.g., Welcome Agent)
    this.setupAutoTransitionListener();
    
    logger.info('[AgentManager] Initialized with agents:', Array.from(this.agents.keys()));
    logger.info('[AgentManager] Ephemeral token mode:', this.useEphemeralTokens);
  }

  /**
   * Setup listener for agent auto-transition events
   */
  private setupAutoTransitionListener(): void {
    window.addEventListener('agent:auto-transition', ((event: CustomEvent) => {
      const { fromAgent, toAgent, context } = event.detail;
      
      logger.info(`[AgentManager] Auto-transition event received: ${fromAgent} → ${toAgent}`);
      
      // Only process if we're currently in the source agent
      if (this.state.currentAgentId === fromAgent) {
        logger.info(`[AgentManager] Triggering auto-transition from ${fromAgent} to ${toAgent}`);
        
        // Trigger the transition
        this.transitionTo(toAgent, context).catch((error: Error) => {
          logger.error(`[AgentManager] Auto-transition failed: ${fromAgent} → ${toAgent}`, error);
        });
      } else {
        logger.warn(`[AgentManager] Ignoring auto-transition event - current agent is ${this.state.currentAgentId}, not ${fromAgent}`);
      }
    }) as EventListener);
  }

  /**
   * Setup event handlers for the GenAI client
   */
  private setupClientEvents(): void {
    this.client.on('open', () => {
      this.state.isConnected = true;
      this.emit('connected');
      this.emitStateChange();
      logger.connection('[AgentManager] Client connected');
    });

    this.client.on('close', () => {
      // Don't emit disconnect during seamless pool transitions
      if (!this.isSeamlessTransition) {
        this.state.isConnected = false;
        this.emit('disconnected');
        this.emitStateChange();
        logger.connection('[AgentManager] Client disconnected');
      } else {
        logger.connection('[AgentManager] Client closed (seamless transition - not emitting disconnect)');
      }
    });

    this.client.on('error', (error) => {
      logger.error('[AgentManager] Client error:', error);
      this.emit('error', new Error(error.message));
    });

    this.client.on('toolcall', async (toolCall) => {
      await this.handleToolCall(toolCall);
    });

    this.client.on('turncomplete', () => {
      logger.info('[AgentManager] Turn complete in agent:', this.state.currentAgentId);
      
      // No fallback - AI must explicitly call tools to transition
      // Greeting agent MUST call set_language_preference to proceed
      if (this.state.currentAgentId === 'greeting_agent') {
        if (this.state.context.transitionData.language) {
          logger.info('[AgentManager] Greeting agent turn complete - language set:', this.state.context.transitionData.language);
        } else {
          logger.warn('[AgentManager] Greeting agent turn complete but no language set - AI must call set_language_preference explicitly');
        }
      }
    });

    this.client.on('content', (content) => {
      // Track assistant messages in history
      if (content.modelTurn?.parts) {
        const textParts = content.modelTurn.parts
          .filter(p => p.text)
          .map(p => p.text)
          .join(' ');
        
        if (textParts) {
          this.addMessageToHistory('assistant', textParts);
        }
      }
    });
  }

  /**
   * Emit state change event
   */
  private emitStateChange(): void {
    this.emit('stateChanged', { ...this.state });
  }

  /**
   * Get current state (immutable copy)
   */
  public getState(): AgentManagerState {
    return { ...this.state };
  }

  /**
   * Get current agent
   */
  public getCurrentAgent(): AgentNode | null {
    if (!this.state.currentAgentId) return null;
    return this.agents.get(this.state.currentAgentId) || null;
  }

  /**
   * Get current context
   */
  public getContext(): AgentContext {
    return { ...this.state.context };
  }

  /**
   * Update transition data (for UI-triggered updates)
   */
  public updateTransitionData(data: Partial<TransitionData>): void {
    this.state.context.transitionData = {
      ...this.state.context.transitionData,
      ...data,
    };
    this.emitStateChange();
  }

  /**
   * Set language preference
   */
  public setLanguage(language: SupportedLanguage): void {
    this.state.context.transitionData.language = language;
    this.emitStateChange();
    logger.info('[AgentManager] Language set to:', language);
  }

  /**
   * Add message to conversation history
   */
  public addMessageToHistory(role: 'user' | 'assistant' | 'system', content: string): void {
    this.state.context.conversationHistory = addToHistory(
      this.state.context.conversationHistory,
      { role, content, agentId: this.state.currentAgentId || undefined }
    );
  }

  /**
   * Initialize and start with the initial agent
   */
  public async start(): Promise<boolean> {
    if (this.isInitialized) {
      logger.warn('[AgentManager] Already initialized');
      return true;
    }

    const initialAgentId = Array.from(this.agents.keys())[0];
    if (!initialAgentId) {
      throw new Error('No agents registered');
    }

    logger.info('[AgentManager] Starting with initial agent:', initialAgentId);
    
    const success = await this.activateAgent(initialAgentId);
    if (success) {
      this.isInitialized = true;
    }
    
    return success;
  }

  /**
   * Stop and cleanup
   */
  public async stop(): Promise<void> {
    logger.info('[AgentManager] Stopping...');
    
    if (this.state.currentAgentId) {
      const currentAgent = this.getCurrentAgent();
      if (currentAgent?.onDeactivate) {
        await currentAgent.onDeactivate(this.state.context);
      }
    }
    
    this.client.disconnect();
    
    // Clean up connection pool
    if (this.connectionPool) {
      this.connectionPool.destroy();
      this.connectionPool = null;
    }
    
    // Reset all state for fresh reconnection
    this.state.currentAgentId = null;
    this.state.isConnected = false;
    this.state.isTransitioning = false;
    this.isInitialized = false;
    
    // Clear conversation history to prevent repetition on reconnect
    this.state.context.conversationHistory = { messages: [] };
    
    // Clear transition data except language preference and user data
    const { language, userData, voiceGender } = this.state.context.transitionData;
    this.state.context.transitionData = {
      language,
      userData,
      voiceGender,
    };
    
    logger.info('[AgentManager] State reset - ready for fresh connection');
    this.emitStateChange();
  }

  /**
   * Activate a specific agent
   */
  private async activateAgent(agentId: string): Promise<boolean> {
    const agent = this.agents.get(agentId);
    if (!agent) {
      logger.error('[AgentManager] Agent not found:', agentId);
      return false;
    }

    // Validate entry guardrails
    const errors = validateEntryGuardrails(agent.entryGuardrails, this.state.context);
    if (errors.length > 0) {
      logger.error('[AgentManager] Entry guardrails failed for agent:', agentId, errors);
      return false;
    }

    logger.info('[AgentManager] Activating agent:', agentId);

    // Disconnect existing connection
    this.client.disconnect();

    if (this.useEphemeralTokens) {
      // EPHEMERAL TOKEN MODE: Fetch new token and create new client
      return this.activateAgentWithToken(agentId, agent);
    } else {
      // LEGACY MODE: Use existing client with new config
      return this.activateAgentLegacy(agentId, agent);
    }
  }

  /**
   * Build context data to pass to backend for system instruction injection
   * This preserves conversation state across agent transitions
   */
  private buildContextForToken(): TokenContextData {
    const { transitionData, conversationHistory } = this.state.context;
    const { language, userData, userLocation, pendingQuestion } = transitionData;

    const contextData: TokenContextData = {};

    if (language) {
      contextData.language = language;
    }

    const conversationSummary = getHistoryText(conversationHistory, 5);
    if (conversationSummary) {
      contextData.conversation_summary = conversationSummary;
    }

    if (userData) {
      contextData.user_name = userData.username;
      contextData.user_mobile = userData.mobileNumber;
    }

    if (userLocation?.address) {
      contextData.user_location = userLocation.address;
    }

    if (pendingQuestion) {
      contextData.pending_question = pendingQuestion;
    }

    return contextData;
  }

  /**
   * Detect context changes between pooled connection and current context
   * Returns a system message to inject if context has changed, null otherwise
   */
  private detectContextChanges(pooledContext: TokenContextData, currentContext: TokenContextData): string | null {
    const changes: string[] = [];
    
    // 1. LANGUAGE (Critical - affects all flows)
    if (currentContext.language && pooledContext.language !== currentContext.language) {
      const langNames: Record<string, string> = { 
        'ar': 'Arabic (UAE Dialect)', 
        'en': 'English', 
        'ur': 'Urdu' 
      };
      changes.push(`**CRITICAL CONTEXT UPDATE:**`);
      changes.push(`**User Language:** ${currentContext.language} (${langNames[currentContext.language]})`);
      changes.push(`**You MUST speak ONLY in ${langNames[currentContext.language]} from now on.**`);
      if (currentContext.language === 'ar') {
        changes.push(`**CRITICAL: Use UAE Emirati dialect for Arabic.**`);
      }
    }
    
    // 2. USER DATA (affects all flows)
    if (currentContext.user_name && pooledContext.user_name !== currentContext.user_name) {
      changes.push(`**User Name:** ${currentContext.user_name}`);
    }
    if (currentContext.user_mobile && pooledContext.user_mobile !== currentContext.user_mobile) {
      changes.push(`**User Mobile:** ${currentContext.user_mobile}`);
    }
    if (currentContext.user_location && pooledContext.user_location !== currentContext.user_location) {
      changes.push(`**User Location:** ${currentContext.user_location}`);
    }
    
    // 3. FAQ FLOW DATA
    if (currentContext.pending_question && !pooledContext.pending_question) {
      changes.push(`\n**PENDING QUESTION (ANSWER THIS IMMEDIATELY!):**`);
      changes.push(`"${currentContext.pending_question}"`);
    }
    
    // 4. CONVERSATION HISTORY (only if significantly different)
    if (currentContext.conversation_summary && pooledContext.conversation_summary !== currentContext.conversation_summary) {
      changes.push(`\n**Previous Conversation Context:**`);
      changes.push(currentContext.conversation_summary);
    }
    
    return changes.length > 0 ? changes.join('\n') : null;
  }

  /**
   * Activate agent using ephemeral token (secure mode)
   * Uses connection pool for instant transitions when available
   */
  private async activateAgentWithToken(agentId: string, agent: AgentNode): Promise<boolean> {
    logger.info('[AgentManager] Activating agent with ephemeral token:', agentId);
    
    // DON'T emit reconnecting yet - wait to see if we have a pool connection

    try {
      // Build context data from current state
      const contextData = this.buildContextForToken();
      logger.info('[AgentManager] Context data for token:', 
        contextData.language ? `language=${contextData.language}` : 'no language',
        contextData.conversation_summary ? 'with history' : 'no history'
      );
      
      // Mark as seamless transition - suppress disconnect events
      this.isSeamlessTransition = true;
      
      // TRY 1: Check if we have a pre-warmed connection in the pool (INSTANT)
      const warmConnection = this.connectionPool?.getWarmConnection(agentId);
      if (warmConnection && warmConnection.isConnected) {
        logger.info('[AgentManager] 🚀 SEAMLESS INSTANT SWITCH - Using pre-warmed connection for:', agentId);
        
        // Disconnect old client (close event suppressed by isSeamlessTransition flag)
        this.client.disconnect();
        
        // Use the pre-warmed client
        this.client = warmConnection.client;
        this.model = warmConnection.model;
        
        // Re-bind client events to this manager
        this.setupClientEvents();
        
        // Keep isConnected = true (don't change it if already connected)
        if (!this.state.isConnected) {
          this.state.isConnected = true;
          this.emit('connected');
        }
        
        // Update state
        this.state.currentAgentId = agentId;
        this.state.context.previousAgentId = this.state.currentAgentId;
        this.emitStateChange();

        // Check if context changed since connection was created
        const contextDelta = this.detectContextChanges(warmConnection.context, contextData);
        if (contextDelta) {
          logger.info('[AgentManager] Context changed, injecting update into connection');
          // Send context update as system message (invisible to user)
          // Use turnComplete: false so AI waits for initial prompt before responding
          this.client.send({ text: `[SYSTEM CONTEXT UPDATE - Do not read this aloud]\n${contextDelta}` }, false);
          // Small delay before initial prompt to let AI process context
          await new Promise(resolve => setTimeout(resolve, 200));
        }

        // Call onActivate hook
        if (agent.onActivate) {
          await agent.onActivate(this.state.context);
        }

        this.emit('agentActivated', agentId, this.state.context);
        logger.info('[AgentManager] Agent activated (seamless instant) with token:', agentId);

        // Send initial prompt
        const initialPrompt = warmConnection.initialPrompt || agent.initialPrompt;
        if (initialPrompt) {
          logger.info('[AgentManager] Sending initial prompt for agent:', agentId);
          await new Promise(resolve => setTimeout(resolve, 50));
          this.client.send({ text: initialPrompt }, true);
        }

        // Warm pool for next likely agents in background
        this.warmPoolForNextAgents(agentId, contextData);

        // Reset seamless flag
        this.isSeamlessTransition = false;
        return true;
      }
      
      // TRY 2: Check if connection is pending (wait for it)
      const pendingConnection = await this.connectionPool?.waitForConnection(agentId);
      if (pendingConnection && pendingConnection.isConnected) {
        logger.info('[AgentManager] ⏳ SEAMLESS PENDING SWITCH - Using pending connection for:', agentId);
        
        // Disconnect old client (close event suppressed by isSeamlessTransition flag)
        this.client.disconnect();
        
        this.client = pendingConnection.client;
        this.model = pendingConnection.model;
        this.setupClientEvents();
        
        // Keep isConnected = true (don't change it if already connected)
        if (!this.state.isConnected) {
          this.state.isConnected = true;
          this.emit('connected');
        }
        
        this.state.currentAgentId = agentId;
        this.state.context.previousAgentId = this.state.currentAgentId;
        this.emitStateChange();

        // Inject context delta if needed
        const contextDelta = this.detectContextChanges(pendingConnection.context, contextData);
        if (contextDelta) {
          logger.info('[AgentManager] Context changed for pending connection, injecting update');
          // Use turnComplete: false so AI waits for initial prompt before responding
          this.client.send({ text: `[SYSTEM CONTEXT UPDATE - Do not read this aloud]\n${contextDelta}` }, false);
          await new Promise(resolve => setTimeout(resolve, 200));
        }

        if (agent.onActivate) {
          await agent.onActivate(this.state.context);
        }

        this.emit('agentActivated', agentId, this.state.context);
        
        const initialPrompt = pendingConnection.initialPrompt || agent.initialPrompt;
        if (initialPrompt) {
          await new Promise(resolve => setTimeout(resolve, 50));
          this.client.send({ text: initialPrompt }, true);
        }

        this.warmPoolForNextAgents(agentId, contextData);
        
        // Reset seamless flag
        this.isSeamlessTransition = false;
        return true;
      }
      
      // TRY 3: Fallback - Fetch new token (cold path - still seamless!)
      logger.info('[AgentManager] ❄️ SEAMLESS COLD PATH - Fetching new token for:', agentId);
      
      // DON'T reset seamless flag - keep UI seamless even for cold path
      // DON'T emit reconnecting - user shouldn't see connecting state
      
      // Disconnect old client silently (close event still suppressed)
      this.client.disconnect();
      
      // Fetch new token for this agent WITH context
      const tokenData: TokenResponse = await fetchGeminiToken(
        this.backendUrl,
        agentId,
        this.voiceGender,
        contextData
      );
      
      logger.info('[AgentManager] Token fetched for agent:', agentId);
      logger.info('[AgentManager] Model locked to:', tokenData.model);
      
      // Create new client with the token
      this.client = new GenAILiveClient({ token: tokenData.token });
      
      // Re-bind client events
      this.setupClientEvents();
      
      // Connect (config is locked in token, so we just need to connect)
      const connected = await this.client.connect(tokenData.model, {});
      if (!connected) {
        logger.error('[AgentManager] Failed to connect with token for agent:', agentId);
        this.isSeamlessTransition = false; // Reset on failure
        return false;
      }
      
      // Update model to what was returned by token
      this.model = tokenData.model;
      
      // Keep isConnected = true if already connected (seamless)
      if (!this.state.isConnected) {
        this.state.isConnected = true;
        this.emit('connected');
      }
      
      // Update state
      this.state.currentAgentId = agentId;
      this.state.context.previousAgentId = this.state.currentAgentId;
      this.emitStateChange();

      // Call onActivate hook
      if (agent.onActivate) {
        await agent.onActivate(this.state.context);
      }

      this.emit('agentActivated', agentId, this.state.context);
      logger.info('[AgentManager] Agent activated (seamless cold path) with token:', agentId);

      // Send initial prompt (use from token response or agent)
      const initialPrompt = tokenData.initial_prompt || agent.initialPrompt;
      if (initialPrompt) {
        logger.info('[AgentManager] Sending initial prompt for agent:', agentId);
        await new Promise(resolve => setTimeout(resolve, 100));
        this.client.send({ text: initialPrompt }, true);
      }

      // Warm pool for next likely agents in background
      this.warmPoolForNextAgents(agentId, contextData);

      // Reset seamless flag at the end
      this.isSeamlessTransition = false;
      return true;
      
    } catch (error) {
      this.isSeamlessTransition = false; // Reset flag on error
      logger.error('[AgentManager] Token fetch/connect error for agent:', agentId, error);
      this.emit('error', new Error(`Failed to activate agent ${agentId}: ${error}`));
      return false;
    }
  }

  /**
   * Warm the connection pool for likely next agents
   * Called after an agent is activated to pre-connect next likely agents
   */
  private warmPoolForNextAgents(currentAgentId: string, context: TokenContextData): void {
    if (!this.connectionPool) return;
    
    logger.info('[AgentManager] Warming pool for likely next agents from:', currentAgentId);
    
    // Fire and forget - don't block activation
    this.connectionPool.warmPool(currentAgentId, context).catch(error => {
      logger.warn('[AgentManager] Pool warming failed:', error);
    });
  }

  /**
   * Activate agent using legacy mode (API key, client reuse)
   */
  private async activateAgentLegacy(agentId: string, agent: AgentNode): Promise<boolean> {
    // Build the config for this agent
    const config = this.buildAgentConfig(agent);

    // Connect with new config
    try {
      const connected = await this.client.connect(this.model, config);
      if (!connected) {
        logger.error('[AgentManager] Failed to connect for agent:', agentId);
        return false;
      }
    } catch (error) {
      logger.error('[AgentManager] Connection error for agent:', agentId, error);
      return false;
    }

    // Update state
    this.state.currentAgentId = agentId;
    this.state.context.previousAgentId = this.state.currentAgentId;
    this.emitStateChange();

    // Call onActivate hook
    if (agent.onActivate) {
      await agent.onActivate(this.state.context);
    }

    this.emit('agentActivated', agentId, this.state.context);
    logger.info('[AgentManager] Agent activated:', agentId);

    // Send initial prompt to trigger AI to speak first
    if (agent.initialPrompt) {
      logger.info('[AgentManager] Sending initial prompt for agent:', agentId);
      await new Promise(resolve => setTimeout(resolve, 100));
      this.client.send({ text: agent.initialPrompt }, true);
    }

    return true;
  }

  /**
   * Build LiveConnectConfig for an agent (LEGACY MODE only)
   * In ephemeral token mode, config comes from backend
   */
  private buildAgentConfig(agent: AgentNode): LiveConnectConfig {
    const voiceGender = this.state.context.transitionData.voiceGender || 'male';
    
    // Inject context into system instruction if needed
    let systemInstruction = agent.systemInstruction || '';
    
    // Add conversation history context
    const historyText = getHistoryText(this.state.context.conversationHistory, 5);
    if (historyText) {
      systemInstruction = `${systemInstruction}\n\n**Previous Conversation Context:**\n${historyText}`;
    }
    
    // Add transition data context
    const { language, userData, userLocation } = this.state.context.transitionData;
    if (language) {
      systemInstruction = `${systemInstruction}\n\n**User Language:** ${language}`;
    }
    if (userData) {
      systemInstruction = `${systemInstruction}\n**User Name:** ${userData.username}\n**User Mobile:** ${userData.mobileNumber}`;
    }
    if (userLocation?.address) {
      systemInstruction = `${systemInstruction}\n**User Location:** ${userLocation.address}`;
    }

    // Add pending question for FAQ
    const pendingQuestion = this.state.context.transitionData.pendingQuestion;
    if (pendingQuestion) {
      systemInstruction = `${systemInstruction}\n\n**PENDING QUESTION (ANSWER THIS IMMEDIATELY!):**\n"${pendingQuestion}"`;
      // Clear the pending question after injecting
      this.state.context.transitionData.pendingQuestion = undefined;
    }
    
    return buildLiveConfig({
      systemInstruction,
      tools: agent.tools || [],
      voiceGender,
      responseModality: agent.responseModality,
    });
  }

  /**
   * Transition to a new agent
   */
  public async transitionTo(
    targetAgentId: string,
    reason: string,
    additionalData?: Partial<TransitionData>
  ): Promise<boolean> {
    if (this.state.isTransitioning) {
      logger.warn('[AgentManager] Already transitioning, ignoring request');
      return false;
    }

    const currentAgentId = this.state.currentAgentId || 'none';
    
    logger.info('[AgentManager] Transitioning from', currentAgentId, 'to', targetAgentId);
    
    this.state.isTransitioning = true;
    this.emitStateChange();

    const transitionEvent: AgentTransitionEvent = {
      fromAgentId: currentAgentId,
      toAgentId: targetAgentId,
      reason,
      context: { ...this.state.context },
      timestamp: new Date(),
    };

    this.emit('transitionStarted', transitionEvent);

    try {
      // Update transition data if provided
      if (additionalData) {
        this.state.context.transitionData = {
          ...this.state.context.transitionData,
          ...additionalData,
        };
      }
      
      this.state.context.transitionReason = reason;

      // Deactivate current agent
      const currentAgent = this.getCurrentAgent();
      if (currentAgent?.onDeactivate) {
        await currentAgent.onDeactivate(this.state.context);
      }
      this.emit('agentDeactivated', currentAgentId, this.state.context);

      // Activate new agent
      const success = await this.activateAgent(targetAgentId);
      
      if (success) {
        this.state.transitionHistory.push(transitionEvent);
        this.emit('transitionCompleted', transitionEvent);
        logger.info('[AgentManager] Transition completed to:', targetAgentId);
      } else {
        this.emit('transitionFailed', transitionEvent, ['Failed to activate agent']);
        logger.error('[AgentManager] Transition failed to:', targetAgentId);
      }

      return success;
    } finally {
      this.state.isTransitioning = false;
      this.emitStateChange();
    }
  }

  /**
   * Detect when a flow has completed and emit event
   * This is used for embedded mode to notify parent of completion
   */
  private detectFlowCompletion(toolName: string, result: ToolCallResult, agentId: string | null): void {
    if (!agentId) return;

    // FAQ flow completion - when answer is provided
    if (agentId === 'faq_agent' && toolName === 'faq_answered') {
      const faqData = result.result;
      this.emit('flowCompleted', 'faq', {
        ...faqData,
        status: 'completed',
        flow_type: 'faq'
      }, agentId);
      logger.info('[AgentManager] FAQ flow completed');
    }
  }

  /**
   * Handle tool calls from the current agent
   */
  private async handleToolCall(toolCall: any): Promise<void> {
    const currentAgent = this.getCurrentAgent();
    if (!currentAgent) {
      logger.warn('[AgentManager] Tool call received but no active agent');
      return;
    }

    logger.tool('[AgentManager] Tool call received:', toolCall.functionCalls?.map((fc: any) => fc.name));

    const functionCalls = toolCall.functionCalls || [];
    const responses: any[] = [];

    for (const fc of functionCalls) {
      let result: ToolCallResult;

      // Check if agent has custom handler
      if (currentAgent.handleToolCall) {
        result = await currentAgent.handleToolCall(fc.name, fc.args, this.state.context);
      } else {
        // Default handling
        result = {
          toolName: fc.name,
          args: fc.args,
          result: { success: true },
        };
      }

      // Emit tool call event
      this.emit('toolCall', result);

      // Detect flow completion before transition
      this.detectFlowCompletion(fc.name, result, this.state.currentAgentId);
      
      // Handle transitions triggered by tool calls
      if (result.triggersTransition && result.transitionTo) {
        logger.info('[AgentManager] Tool triggered transition to:', result.transitionTo);
        
        // Transition immediately - connection pooling makes this seamless
        this.transitionTo(
          result.transitionTo!,
          `Tool ${fc.name} triggered transition`,
          result.transitionData
        );
      }

      responses.push({
        response: { output: result.result },
        id: fc.id,
        name: fc.name,
      });
    }

    // Send tool responses
    if (responses.length > 0) {
      this.client.sendToolResponse({ functionResponses: responses });
    }
  }

  /**
   * Request transition by name (for self-managed transitions)
   */
  public async requestTransition(transitionName: string): Promise<boolean> {
    const currentAgent = this.getCurrentAgent();
    if (!currentAgent) {
      logger.warn('[AgentManager] No active agent for transition');
      return false;
    }

    const result = findValidTransition(
      currentAgent.exitGuardrails.transitions,
      transitionName,
      this.state.context
    );

    if (!result) {
      logger.warn('[AgentManager] No transition found for:', transitionName);
      
      // Try fallback
      if (currentAgent.exitGuardrails.fallback) {
        return this.transitionTo(
          currentAgent.exitGuardrails.fallback.agentId,
          `Fallback from ${transitionName}`
        );
      }
      return false;
    }

    if (result.errors.length > 0) {
      logger.error('[AgentManager] Transition guardrails failed:', result.errors);
      return false;
    }

    return this.transitionTo(result.targetAgentId, transitionName);
  }

  /**
   * Send message to current agent
   */
  public send(message: string, turnComplete: boolean = true): void {
    this.addMessageToHistory('user', message);
    this.client.send([{ text: message }], turnComplete);
  }

  /**
   * Send realtime input (audio/video)
   */
  public sendRealtimeInput(chunks: Array<{ mimeType: string; data: string }>): void {
    this.client.sendRealtimeInput(chunks);
  }

  /**
   * Get client for direct access if needed
   */
  public getClient(): GenAILiveClient {
    return this.client;
  }

  /**
   * Check if connected
   */
  public isConnected(): boolean {
    return this.state.isConnected;
  }

  /**
   * Get all registered agents
   */
  public getAgents(): Map<string, AgentNode> {
    return new Map(this.agents);
  }
}

/**
 * Create an AgentManager instance
 */
export function createAgentManager(config: AgentManagerConfig): AgentManager {
  return new AgentManager(config);
}

